import type { PixelCanvas } from '../../../lib';
import { sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * The Main Event's shots (the Luchador set's gun, ADR-0160 amended): a little luchador's mask flying (magenta, gold
 * flames off its white eyes, two gold ribbons fluttering behind), a comic-book smack at the muzzle and a POW where it
 * lands; and the flourish's championship belt flung up (`luchador_belt`).
 */

/** A comic burst's edge at angle a: `n` spikes between radii r0 and r1, each a little different. */
const spiky = (a: number, n: number, r0: number, r1: number, seed: number) => {
  const k = ((((a / (Math.PI * 2)) * n + seed) % 1) + 1) % 1;
  const jag = 0.75 + 0.25 * Math.sin(Math.floor((a / (Math.PI * 2)) * n + seed) * 2.7);
  return r0 + (r1 - r0) * jag * (1 - Math.abs(1 - 2 * k)) ** 1.2;
};

/** The mask (centred a little behind the pellet's head), its ribbons trailing left and waving by frame. */
const flying = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y): Tone => {
    const [dx, dy] = [x - 18, y - 8];
    const e = (dx / 5.5) ** 2 + (dy / 6.2) ** 2;
    if (e < 1) {
      // Its eye holes rimmed in white, flicked back into gold flames.
      for (const ex of [16, 20]) {
        const d = ((x - ex) / 1.3) ** 2 + ((y - 7.5) / 1.6) ** 2;
        if (d < 1) return 'k';
        if (d < 2.2) return 'w';
      }
      if (Math.abs(y - 6 + Math.abs(x - 18) * 0.35) < 0.6 && Math.abs(x - 18) > 3) return 'y';
      // A white crest down its middle, the mouth hole.
      if (Math.abs(x - 18) < 0.9 && y < 6) return 'w';
      if (((x - 18) / 2) ** 2 + ((y - 11) / 1) ** 2 < 1) return 'k';
      return dx + dy < -3 ? 'i' : e > 0.7 && dx + dy > 2 ? 'P' : 'p';
    }
    // Two gold ribbons fluttering off its back.
    for (const [y0, phase] of [
      [6, 0],
      [10, 1.6],
    ] as const) {
      if (x > 13 || x < 2) continue;
      const wave = Math.sin((13 - x) * 0.7 - f * (Math.PI / 2) + phase) * (13 - x) * 0.12;
      if (Math.abs(y - y0 - wave - (13 - x) * 0.08) < 0.75) return x < 6 ? 'Y' : 'y';
    }
    return null;
  });

/** A comic smack: a white heart, a spiky gold star with a red rim forward, then speed lines breaking up. */
const smack = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y): Tone => {
    const [dx, dy] = [x - (9 + f * 3), y - 20];
    const d = Math.hypot(dx, dy * 1.2);
    const edge = spiky(Math.atan2(dy, dx), 9, 3 + f, 10 - f * 2, f * 0.3);
    if (f < 2 && d < edge) return d < edge * 0.4 ? 'w' : d < edge * 0.75 ? 'y' : d < edge - 1 ? 'o' : 'r';
    // Three speed lines out of the muzzle, streaking forward.
    for (const [row, from] of [
      [-5, 9],
      [0, 6],
      [5, 10],
    ] as const) {
      const start = from + f * 4;
      if (f > 0 && Math.abs(dy - row) < 0.6 && dx > start && dx < start + 12 - f * 2) return f === 2 ? '5' : 'w';
    }
    return null;
  });

/** POW: a white flash, a big spiky starburst (yellow heart, orange, a red rim) bursting, breaking into shards. */
const pow = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y): Tone => {
    const [dx, dy] = [x - 22, y - 22];
    const a = Math.atan2(dy, dx);
    const d = Math.hypot(dx, dy);
    if (f === 0) return d < spiky(a, 8, 3, 9, 0.1) ? (d < 4 ? 'w' : 'y') : null;
    const edge = spiky(a, 10, 7 + f * 2, 15 + f * 2, 0.2);
    if (f === 3) {
      // Shards flung out, the heart gone.
      const ring = d > edge * 0.75 && d < edge && Math.floor((a / (Math.PI * 2)) * 20 + 20) % 2 === 0;
      return ring ? (d > edge - 1.5 ? 'r' : 'o') : null;
    }
    if (d >= edge) return null;
    if (d > edge - 1.3) return 'r';
    if (d > edge - 3.2) return 'o';
    if (f === 2 && d < edge * 0.35) return null;
    return d < edge * 0.55 ? (f === 1 ? 'w' : 'y') : 'y';
  });

/** The championship belt (24 × 12, its plate's middle at (12, 6)): black straps, a gold plate, a red jewel. */
const belt = () => (c: PixelCanvas) =>
  paint(c, (x, y): Tone => {
    const e = ((x - 12) / 6.5) ** 2 + ((y - 6) / 4.8) ** 2;
    if (e < 1) {
      const j = ((x - 12) / 1.8) ** 2 + ((y - 6) / 1.5) ** 2;
      if (j < 1) return x < 12 && y < 6 ? 'i' : 'r';
      if (j < 1.9) return 'w';
      if (e > 0.7) return x + y < 17 ? 'Y' : 'U';
      return x + y < 16 ? 'y' : x + y > 20 ? 'U' : 'Y';
    }
    if (y > 3.5 && y < 8.5 && x > 0.5 && x < 23.5) return y < 4.5 ? '3' : y > 7.5 ? 'k' : x % 3 < 1 ? 'Y' : '1';
    return null;
  });

export default [
  sprite({
    name: 'mask_shot',
    ...shot,
    notes: `The Main Event's bullet (ADR-0160 amended): a little luchador's mask flying, magenta (lit pink on its
top-left, purple at its rim), white-rimmed black eye holes flicked into gold flames, a white crest, a mouth hole; two
gold ribbons trailing off its back and waving over 4 frames. Drawn upright. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
    anims: { idle: { fps: 16, frames: frames4(flying) } },
  }),
  sprite({
    name: 'smack_muzzle',
    ...muzzle,
    notes: `The Main Event's muzzle flash (ADR-0160 amended): a comic-book smack, a white heart in a spiky gold star
rimmed orange and red, pushed forward and shrinking, then white speed lines breaking up. Points right. Rubric (4×): S4
R4 P4 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(smack) } },
  }),
  sprite({
    name: 'pow_burst',
    ...burst,
    notes: `The Main Event's hit (ADR-0160 amended): POW, a white flash, then a big spiky comic starburst (a white then
yellow heart, orange, a red rim) bursting, its heart opening, then breaking into shards. 4 frames. Rubric (4×): S4 R4 P4
L4 C4 A4 F4.`,
    anims: { burst: { fps: 16, loop: false, frames: frames4(pow) } },
  }),
  sprite({
    name: 'luchador_belt',
    group: 'fx',
    size: [24, 12],
    origin: [12, 6],
    density: 4,
    notes: `The Luchador's knock-out championship belt (ADR-0160 amended): black leather straps studded in gold, a big
gold plate lit on its top-left, a red jewel in a white ring in its middle. Rubric (4×): S4 R4 P4 L4 C4 F4.`,
    anims: { idle: { fps: 1, frames: [belt()] } },
  }),
];
